/* eslint-disable no-template-curly-in-string */
import { useInjection } from 'inversify-react';
import { useMemo } from 'react';

import { AuthModal } from '@desygner/ui-common-login';
import { useTranslation } from '@desygner/ui-common-translation';

import { IConfigStoreInjector, TYPES } from '../../../inversify.config';
import { IUserStoreInjector } from '../../../store';
import { useResponsive } from '../../hooks/useResponsive';

export function LoginModal({
  onLogin,
}: {
  onLogin: (userToken: string, userHash?: string, apiKey?: string) => void;
}) {
  const configStore = useInjection<IConfigStoreInjector>(TYPES.Store.Config)();
  const { authUrl } = configStore.api;
  const { isTabletOrMobile } = useResponsive();
  const useUserStore = useInjection<IUserStoreInjector>(TYPES.Store.User);
  const { isCredentialValid } = useUserStore();
  const { t } = useTranslation();

  const loginEmailView = useMemo(
    () => ({
      title: t('shared.authModal.login.emailView.title'),
      subtitle: t('shared.authModal.login.emailView.subtitle'),
      form: {
        placeholder: t('shared.authModal.login.emailView.form.placeholder'),
      },
      cta: {
        anotherWay: t('shared.authModal.login.emailView.cta.anotherWay'),
        continueWith: t('shared.authModal.login.emailView.cta.continueWith'),
        continue: t('shared.authModal.login.emailView.cta.continue'),
      },
      or: t('shared.authModal.login.emailView.or'),
      disclaimer: {
        text: t('shared.authModal.login.emailView.disclaimer.text'),
        tosLinkText: t('shared.authModal.login.emailView.disclaimer.tosLinkText'),
        ppLinkText: t('shared.authModal.login.emailView.disclaimer.ppLinkText'),
        and: t('shared.authModal.login.emailView.disclaimer.and'),
      },
      errors: {
        client: {
          required: t('shared.authModal.login.emailView.errors.client.required'),
          email: t('shared.authModal.login.emailView.errors.client.email'),
        },
        e500: t('shared.authModal.e500'),
      },
    }),
    [t]
  );

  const loginPasswordView = useMemo(
    () => ({
      title: t('shared.authModal.login.passwordView.title'),
      subtitle: t('shared.authModal.login.passwordView.subtitle'),
      form: {
        placeholder: t('shared.authModal.login.passwordView.form.placeholder'),
      },
      errors: {
        client: {
          required: t('shared.authModal.login.passwordView.errors.client.required'),
        },
        e401: t('shared.authModal.login.passwordView.errors.e401'),
        e500: t('shared.authModal.e500'),
      },
      cta: {
        continue: t('shared.authModal.login.passwordView.cta.continue'),
        resetPassword: t('shared.authModal.login.passwordView.cta.resetPassword'),
        anotherWay: t('shared.authModal.login.passwordView.cta.anotherWay'),
        continueWith: t('shared.authModal.login.passwordView.cta.continueWith'),
      },
      or: t('shared.authModal.login.passwordView.or'),
      forgotPassword: t('shared.authModal.login.passwordView.forgotPassword'),
      getResetPasswordSuccessMessage: (email: string) =>
        t('shared.authModal.login.passwordView.getResetPasswordSuccessMessage').replace(
          '${email}',
          email
        ),
    }),
    [t]
  );

  const loginAnotherWayView = useMemo(
    () => ({
      continueWith: t('shared.authModal.login.anotherWay.continueWith'),
      disclaimer: {
        text: t('shared.authModal.login.anotherWay.disclaimer.text'),
        tosLinkText: t('shared.authModal.login.anotherWay.disclaimer.tosLinkText'),
        ppLinkText: t('shared.authModal.login.anotherWay.disclaimer.ppLinkText'),
        and: t('shared.authModal.login.anotherWay.disclaimer.and'),
      },
      title: t('shared.authModal.login.anotherWay.title'),
    }),
    [t]
  );

  const signupEmailView = useMemo(
    () => ({
      title: t('shared.authModal.signup.emailView.title'),
      subtitle: t('shared.authModal.signup.emailView.subtitle'),
      form: {
        placeholder: t('shared.authModal.signup.emailView.form.placeholder'),
      },
      cta: {
        anotherWay: t('shared.authModal.signup.emailView.cta.anotherWay'),
        continueWith: t('shared.authModal.signup.emailView.cta.continueWith'),
        continue: t('shared.authModal.signup.emailView.cta.continue'),
      },
      or: t('shared.authModal.signup.emailView.or'),
      disclaimer: {
        text: t('shared.authModal.signup.emailView.disclaimer.text'),
        tosLinkText: t('shared.authModal.signup.emailView.disclaimer.tosLinkText'),
        ppLinkText: t('shared.authModal.signup.emailView.disclaimer.ppLinkText'),
        and: t('shared.authModal.signup.emailView.disclaimer.and'),
      },
      errors: {
        client: {
          required: t('shared.authModal.signup.emailView.errors.client.required'),
          email: t('shared.authModal.signup.emailView.errors.client.email'),
        },
        e500: t('shared.authModal.e500'),
      },
    }),
    [t]
  );

  const signupCodeView = useMemo(
    () => ({
      title: t('shared.authModal.signup.codeView.title'),
      cta: {
        continue: t('shared.authModal.signup.codeView.cta.continue'),
        resend: t('shared.authModal.signup.codeView.cta.resend'),
      },
      form: {
        placeholder: t('shared.authModal.signup.codeView.form.placeholder'),
      },
      didntGetCode: t('shared.authModal.signup.codeView.didntGetCode'),
      getSubtitle: (email: string) =>
        t('shared.authModal.signup.codeView.getSubtitle').replace('${email}', email),
      errors: {
        client: {
          required: t('shared.authModal.signup.codeView.errors.client.required'),
        },
        incorrectCode: t('shared.authModal.signup.codeView.errors.incorrectCode'),
        e500: t('shared.authModal.e500'),
      },
    }),
    [t]
  );

  const signupPassportView = useMemo(
    () => ({
      title: t('shared.authModal.signup.passwordView.title'),
      subtitle: t('shared.authModal.signup.passwordView.subtitle'),
      form: {
        newPasswordLabel: t('shared.authModal.signup.passwordView.form.newPasswordLabel'),
        newPasswordPlaceholder: t(
          'shared.authModal.signup.passwordView.form.newPasswordPlaceholder'
        ),
        marketingLabel: t('shared.authModal.signup.passwordView.form.marketingLabel'),
      },
      cta: {
        continue: t('shared.authModal.signup.passwordView.cta.continue'),
      },
      errors: {
        client: {
          newPasswordMinLenght: t(
            'shared.authModal.signup.passwordView.errors.client.newPasswordMinLenght'
          ),
          newPasswordRequired: t(
            'shared.authModal.signup.passwordView.errors.client.newPasswordRequired'
          ),
        },
        e500: t('shared.authModal.e500'),
      },
    }),
    [t]
  );

  const signupNameView = useMemo(
    () => ({
      title: t('shared.authModal.signup.nameView.title'),
      subtitle: t('shared.authModal.signup.nameView.subtitle'),
      form: {
        label: t('shared.authModal.signup.nameView.form.label'),
        placeholder: t('shared.authModal.signup.nameView.form.placeholder'),
      },
      cta: {
        continue: t('shared.authModal.signup.nameView.cta.continue'),
      },
      errors: {
        e500: t('shared.authModal.e500'),
      },
    }),
    [t]
  );

  const loading = useMemo(() => {
    return {
      loadingMessage: t('shared.authModal.loading.loadingMessage'),
    };
  }, [t]);

  const labels = useMemo(
    () => ({
      login: {
        emailView: loginEmailView,
        passwordView: loginPasswordView,
        anotherWay: loginAnotherWayView,
      },
      signup: {
        emailView: signupEmailView,
        codeView: signupCodeView,
        passwordView: signupPassportView,
        nameView: signupNameView,
      },
      loading: { ...loading },
    }),
    [
      loginAnotherWayView,
      loginEmailView,
      loginPasswordView,
      signupCodeView,
      signupEmailView,
      signupNameView,
      signupPassportView,
      loading,
    ]
  );

  return (
    <AuthModal
      isOpened={!isCredentialValid}
      initView={'loginEmail'}
      onLogin={onLogin}
      apiBaseUrl={authUrl}
      labels={labels}
      isLoginOnly={true}
      isAuthCloseDisabled={true}
      useGoogleTapOnSign={!isTabletOrMobile}
    />
  );
}
